import { Link } from "react-router-dom";
import {
  Check,
  X,
  Sun,
  Camera,
  Smartphone,
  ImageIcon,
  ArrowRight,
  BookOpen,
} from "lucide-react";
import { openEbook } from "@/lib/ebook";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import FAQ from "@/components/FAQ";

const STANDARDS = [
  {
    icon: Sun,
    title: "Lighting",
    body: "Soft, directional light — natural window light, golden hour, or studio-quality flash. Avoid harsh overhead light, mixed light sources, and heavy shadows on the face.",
  },
  {
    icon: ImageIcon,
    title: "Resolution",
    body: "Original, full-resolution files only. Aim for 12MP+ from a phone (iPhone 11 and up) or a DSLR/mirrorless camera. Avoid screenshots, social media downloads, and zoomed-in crops.",
  },
  {
    icon: Camera,
    title: "Composition",
    body: "Subject framed from chest up or full body. Eyes sharp. Background uncluttered. Avoid distracting objects, busy walls, or anything competing with the subject.",
  },
  {
    icon: Smartphone,
    title: "Format",
    body: "JPG or HEIC, untouched. No filters, no Instagram presets, no AI enhancements. We treat each photo from the raw original.",
  },
];

const GOOD = [
  "Natural window light or studio lighting",
  "Eyes sharp and in focus",
  "Subject centered or rule-of-thirds",
  "Original phone or camera file",
  "Clean, uncluttered background",
  "Confident expression or genuine moment",
];

const BAD = [
  "Selfies in mirrors or poor lighting",
  "Heavy filters, AI edits, or Instagram presets",
  "Screenshots or Facebook/IG downloads",
  "Blurry, motion, or low-resolution",
  "Group photos cropped to one subject",
  "Sunglasses, hats covering eyes (unless intentional)",
];

const CHECKLIST = [
  "Original file (not a screenshot or download)",
  "Highest resolution available (12MP+ recommended)",
  "Lighting is soft and even — eyes in light",
  "Subject is sharp, no motion blur",
  "Outfit and background coordinated",
  "Two backup photos in case we need alternates",
];

const FAQS = [
  {
    q: "What if I don't have a high-quality photo?",
    a: "Two options: upgrade to a Royal Essence session where we shoot it for you, or send your best three and we'll tell you within 24 hours which we can work with.",
  },
  {
    q: "Can I send a photo from Instagram or Facebook?",
    a: "We strongly recommend against it. Social platforms compress images significantly. Send the original from your camera roll or cloud storage.",
  },
  {
    q: "Do you do photo restoration?",
    a: "Yes — for vintage and damaged family photos. Send what you have and we'll quote restoration before artistry.",
  },
];

export default function PhotoGuide() {
  return (
    <div className="relative">
      <SEO
        title="Photo Standards for Custom Artwork | Crowned Canvas Creations"
        description="Photo submission standards for your Crowned custom canvas — resolution, lighting and composition guidance from Crowned Canvas Creations."
        path="/photo-guide"
      />
      <Header />
      <main className="pt-24 md:pt-32">
        {/* HERO */}
        <section className="relative">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_15%_30%,rgba(130,73,55,0.25),transparent_60%)]" aria-hidden />
          <div className="relative max-w-[1100px] mx-auto px-6 md:px-12 py-12 md:py-20">
            <p className="overline">Photo Submission Standards</p>
            <h1 className="font-serif-display text-4xl md:text-5xl lg:text-[80px] leading-[1] mt-5 max-w-3xl">
              Send us a photo<br />
              <span className="italic text-[#D4AF37]">worth framing.</span>
            </h1>
            <p className="text-white/70 mt-6 max-w-2xl font-light leading-relaxed text-base md:text-lg">
              Selecting the right photo is the difference between a canvas that looks pretty and a canvas that looks like a legacy. This is exactly what our studio looks for before turning your image into a Royal Essence or Sovereign piece.
            </p>
            <div className="flex flex-wrap gap-3 mt-9">
              <button
                type="button"
                onClick={() => openEbook("photo-guide-hero")}
                data-testid="photoguide-ebook-btn"
                className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-7 py-4 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all"
              >
                <BookOpen className="w-4 h-4" />
                Read The Full Free Guide
              </button>
              <Link
                to="/collection/sovereign"
                className="inline-flex items-center gap-3 border border-white/20 text-white/85 px-7 py-4 text-xs uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
              >
                Order Sovereign Canvas
              </Link>
            </div>
          </div>
        </section>

        {/* Quality Standards */}
        <section data-testid="photoguide-standards" className="py-16 md:py-24 border-t border-white/5 bg-black/40">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
            <div className="mb-10">
              <p className="overline">Quality Standards</p>
              <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl mt-4 leading-[1.05] max-w-2xl">
                Four things we check first.
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-white/10">
              {STANDARDS.map((s) => {
                const Icon = s.icon;
                return (
                  <div
                    key={s.title}
                    className="p-7 md:p-8 border-b lg:border-b-0 lg:border-r border-white/10 last:border-r-0 hover:bg-white/[0.02] transition-colors"
                  >
                    <Icon className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
                    <h3 className="font-serif-display text-xl mt-5">{s.title}</h3>
                    <p className="text-white/55 text-sm mt-3 font-light leading-relaxed">{s.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Good vs Poor */}
        <section data-testid="photoguide-examples" className="py-20 md:py-28 border-t border-white/5">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <div className="grid md:grid-cols-2 gap-px bg-white/5 border border-white/10">
              <div className="bg-black p-8 md:p-10">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 border border-[#D4AF37]/50 bg-[#D4AF37]/10 flex items-center justify-center">
                    <Check className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <p className="overline text-[#D4AF37]">Good Photos</p>
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl mt-5">What we love.</h3>
                <ul className="mt-6 space-y-3">
                  {GOOD.map((g) => (
                    <li key={g} className="flex items-start gap-3 text-sm text-white/80 font-light">
                      <Check className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span>{g}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-black p-8 md:p-10">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 border border-white/20 bg-white/5 flex items-center justify-center">
                    <X className="w-4 h-4 text-white/60" />
                  </div>
                  <p className="overline text-white/55">Poor Photos</p>
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl mt-5">What we'll politely decline.</h3>
                <ul className="mt-6 space-y-3">
                  {BAD.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm text-white/55 font-light">
                      <X className="w-4 h-4 text-white/40 mt-1 flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Submission Checklist */}
        <section data-testid="photoguide-checklist" className="py-16 md:py-24 border-t border-white/5 bg-black/40">
          <div className="max-w-[900px] mx-auto px-6 md:px-12">
            <p className="overline">Before You Submit</p>
            <h2 className="font-serif-display text-3xl md:text-4xl mt-4 leading-tight">
              Submission checklist.
            </h2>
            <p className="text-white/60 mt-4 font-light leading-relaxed">
              Run through this before sending your photos. Five minutes here saves a week of back-and-forth.
            </p>
            <ul className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-4">
              {CHECKLIST.map((c, i) => (
                <li key={c} className="flex items-start gap-3 text-sm md:text-base text-white/85 font-light border-b border-white/5 pb-3">
                  <span className="overline text-[#D4AF37] w-6 flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => openEbook("photo-guide-checklist")}
                className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-7 py-4 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all"
              >
                Read The Full Guide
                <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 border border-white/20 text-white/85 px-7 py-4 text-xs uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
              >
                Submit My Photo
              </Link>
            </div>
          </div>
        </section>

        <FAQ items={FAQS} title="Photo questions." testidPrefix="photoguide-faq" />
      </main>
      <Footer />
    </div>
  );
}
